/**
 * features/71c/AuditExportPanel.tsx — MER Algo Footprints v7.1C
 *
 * Audit Export Package panel.
 * Aggregates all available Phase 7.1A/B/C data into a single, audit-ready
 * export UI. All exports are client-side (Blob download). No server calls.
 *
 * Exports available:
 *   CSV:
 *     - Full activity log (Journal71A entries)
 *     - Analytics summary (symbol / setup / regime breakdowns)
 *     - Outcome report (attribution-linked outcomes)
 *   JSON:
 *     - Full activity log
 *     - Discipline score breakdown
 *     - Session bundle (summary + entries + discipline + outcome report)
 *     - Executive summary
 *
 * INVARIANTS:
 *   - Read-only — no engine calls, no store writes
 *   - Export never feeds back into any decision logic
 *   - All data is from journal71aEntries (already-computed stored data)
 */

import React, { useMemo, useState } from 'react';
import { useStore } from '../../store';
import { exportJournalCSV, exportJournalJSON, exportDisciplineJSON } from '../71b/export71b';
import {
  buildSessionSummary, buildExecutiveSummary, buildOutcomeReport, groupBySession,
} from './sessionUtils';
import { Journal71AEntry } from '../../types';

// ─── Local discipline derivation (mirrors DisciplineScorePanel rules) ─────────

function deriveDiscipline(entries: Journal71AEntry[]): { score: number; grade: string; rules: any[]; observations: string[] } {
  if (!entries.length) return { score: 0, grade: 'No Data', rules: [], observations: [] };

  const r1 = entries.filter(e => e.actionType === 'note_added').length;
  const r2 = entries.filter(e => e.actionType === 'outcome_reviewed').length;
  const r3 = entries.filter(e => e.actionType === 'trade_skipped' && (e.score ?? 100) < 45).length;
  const r4 = entries.filter(e => e.actionType === 'setup_viewed').length;
  const p1 = entries.filter(e => e.actionType === 'trade_taken' && (e.score ?? 100) < 45 && !e.note).length;
  const takenHigh = entries.filter(e => e.actionType === 'trade_taken' && (e.score ?? 0) >= 65);
  const outcomeSyms = new Set(entries.filter(e => e.actionType === 'outcome_reviewed').map(e => e.symbol));
  const p2 = takenHigh.filter(e => !outcomeSyms.has(e.symbol)).length;

  const rules = [
    { id: 'R1', label: 'Note added',               detail: 'Adding context notes',                value: 3,  count: r1, total: r1 * 3  },
    { id: 'R2', label: 'Outcome recorded',          detail: 'Reviewing and recording outcomes',     value: 4,  count: r2, total: r2 * 4  },
    { id: 'R3', label: 'Skipped weak setup',        detail: 'Passing on low-score (<45) setups',    value: 2,  count: r3, total: r3 * 2  },
    { id: 'R4', label: 'Reviewed before action',    detail: 'Opening attribution before trading',   value: 2,  count: r4, total: r4 * 2  },
    { id: 'P1', label: 'Took weak setup unchecked', detail: 'Took low-score setup, no note',        value: -3, count: p1, total: p1 * -3 },
    { id: 'P2', label: 'Missing outcome review',    detail: 'Took high-score setup, no outcome',    value: -1, count: p2, total: p2 * -1 },
  ];
  const raw = rules.reduce((s, r) => s + r.total, 0);
  const score = Math.min(100, Math.max(0, raw));
  const grade = score >= 80 ? 'Elite' : score >= 60 ? 'Strong' : score >= 35 ? 'Developing' : 'Needs Work';

  const obs: string[] = [];
  if (r2 === 0 && entries.filter(e => e.actionType === 'trade_taken').length > 0)
    obs.push('No outcomes recorded for taken trades — review completion pending.');
  if (p1 > 0) obs.push(`${p1} taken setup${p1 > 1 ? 's' : ''} with score <45 and no note.`);
  if (p2 > 0) obs.push(`${p2} high-score taken setup${p2 > 1 ? 's' : ''} without an outcome review.`);

  return { score, grade, rules, observations: obs };
}

// ─── Download helpers ─────────────────────────────────────────────────────────

function downloadBlob(content: string, filename: string, mime: string): void {
  const blob = new Blob([content], { type: mime });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function ts(): string {
  return new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
}

// ─── CSV builders ─────────────────────────────────────────────────────────────

function buildAnalyticsSummaryCSV(entries: Journal71AEntry[]): string {
  const fmt = (v: any) => {
    const s = String(v ?? '');
    return s.includes(',') || s.includes('"') || s.includes('\n')
      ? `"${s.replace(/"/g, '""')}"` : s;
  };

  // Symbol breakdown
  const symMap = new Map<string, { taken: number; skipped: number; noted: number; outcomes: number }>();
  for (const e of entries) {
    if (!symMap.has(e.symbol)) symMap.set(e.symbol, { taken: 0, skipped: 0, noted: 0, outcomes: 0 });
    const r = symMap.get(e.symbol)!;
    if (e.actionType === 'trade_taken')       r.taken++;
    if (e.actionType === 'trade_skipped')     r.skipped++;
    if (e.actionType === 'note_added')        r.noted++;
    if (e.actionType === 'outcome_reviewed')  r.outcomes++;
  }

  const rows = [
    ['Symbol', 'Taken', 'Skipped', 'Noted', 'Outcomes', 'SelectionRate'].join(','),
    ...[...symMap.entries()].sort((a, b) => (b[1].taken + b[1].skipped) - (a[1].taken + a[1].skipped))
      .map(([sym, r]) => {
        const decided = r.taken + r.skipped;
        const rate = decided ? `${Math.round(r.taken / decided * 100)}%` : '—';
        return [fmt(sym), r.taken, r.skipped, r.noted, r.outcomes, rate].join(',');
      }),
  ];
  return rows.join('\n');
}

function buildOutcomeReportCSV(rows: ReturnType<typeof buildOutcomeReport>): string {
  const fmt = (v: any) => {
    const s = String(v ?? '');
    return s.includes(',') || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = ['Timestamp','Symbol','Setup','Direction','Score','Confidence',
    'Regime','Gamma','Action','HasNote','NotePreview','Outcome','SnapshotRank'].join(',');
  const dataRows = rows.map(r => [
    fmt(r.loggedAt), fmt(r.symbol), fmt(r.setupType), fmt(r.direction),
    r.score ?? '', r.confidence ?? '',
    fmt(r.regime), fmt(r.gammaStatus), fmt(r.action),
    r.hasNote ? 'yes' : 'no', fmt(r.notePreview), fmt(r.outcome),
    r.snapshotRank ?? '',
  ].join(','));
  return [header, ...dataRows].join('\n');
}

// ─── Export action button ─────────────────────────────────────────────────────

function ExportRow({ label, description, onExport, disabled }: {
  label: string; description: string;
  onExport: () => void; disabled?: boolean;
}) {
  const [done, setDone] = useState(false);

  const handle = () => {
    if (disabled) return;
    onExport();
    setDone(true);
    setTimeout(() => setDone(false), 2500);
  };

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '7px 0', borderBottom: '1px solid var(--surface)',
    }}>
      <div>
        <div style={{ fontSize: 10, color: 'var(--text)', fontFamily: 'var(--mono)' }}>{label}</div>
        <div style={{ fontSize: 8, color: 'var(--t3)', marginTop: 1 }}>{description}</div>
      </div>
      <button
        onClick={handle}
        disabled={disabled}
        style={{
          fontSize: 9, fontFamily: 'var(--mono)', fontWeight: 700,
          color: done ? 'var(--green)' : disabled ? 'var(--t3)' : 'var(--blue-dim)',
          border: `1px solid ${done ? 'var(--green)44' : disabled ? 'var(--surface)' : 'var(--border)'}`,
          background: done ? 'var(--green)10' : 'none',
          padding: '3px 10px', borderRadius: 3, cursor: disabled ? 'default' : 'pointer',
          minWidth: 64, textAlign: 'center',
        }}>
        {done ? '✓ done' : '⬇ export'}
      </button>
    </div>
  );
}

// ─── Main panel ───────────────────────────────────────────────────────────────

export function AuditExportPanel() {
  const allEntries = useStore(s => s.journal71aEntries);
  const [selectedSession, setSelectedSession] = useState<string>('__all__');

  const sessions = useMemo(() => groupBySession(allEntries), [allEntries]);

  const activeEntries = useMemo(() => {
    if (selectedSession === '__all__') return allEntries;
    return sessions.find(s => s.label === selectedSession)?.entries ?? [];
  }, [allEntries, sessions, selectedSession]);

  const { score: disciplineScore, grade: disciplineGrade, rules, observations } = useMemo(
    () => deriveDiscipline(activeEntries), [activeEntries],
  );

  const summary = useMemo(
    () => buildSessionSummary(activeEntries, disciplineScore, disciplineGrade),
    [activeEntries, disciplineScore, disciplineGrade],
  );

  const execSummary = useMemo(() => buildExecutiveSummary(summary), [summary]);
  const outcomeRows = useMemo(() => buildOutcomeReport(activeEntries), [activeEntries]);

  const none = !activeEntries.length;
  const label = selectedSession === '__all__' ? 'all-time' : selectedSession;

  const sel: React.CSSProperties = {
    background: 'var(--surface2)', border: '1px solid var(--border)',
    borderRadius: 3, color: 'var(--text)', fontFamily: 'var(--mono)',
    fontSize: 11, padding: '4px 8px', outline: 'none', cursor: 'pointer',
  };

  return (
    <div>
      {/* Session selector */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <span style={{ fontSize: 9, color: 'var(--t3)', fontFamily: 'var(--mono)',
          textTransform: 'uppercase', letterSpacing: '.5px' }}>Scope</span>
        <select value={selectedSession} onChange={e => setSelectedSession(e.target.value)} style={sel}>
          <option value="__all__">All time ({allEntries.length} entries)</option>
          {sessions.map(s => (
            <option key={s.label} value={s.label}>{s.label} ({s.entries.length})</option>
          ))}
        </select>
        {none && (
          <span style={{ fontSize: 9, color: 'var(--amber)', fontFamily: 'var(--mono)' }}>
            No data for this selection
          </span>
        )}
      </div>

      {/* ── CSV exports ── */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 9, color: 'var(--t3)', fontFamily: 'var(--mono)',
          textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 8 }}>
          CSV Exports
        </div>
        <ExportRow
          label="Activity Log (CSV)"
          description="All Journal71A entries — timestamp, symbol, action, setup, score, outcome, note"
          disabled={none}
          onExport={() => exportJournalCSV(activeEntries)}
        />
        <ExportRow
          label="Analytics Summary (CSV)"
          description="Per-symbol breakdown — taken, skipped, noted, outcome counts, selection rate"
          disabled={none}
          onExport={() => downloadBlob(
            buildAnalyticsSummaryCSV(activeEntries),
            `MER-analytics-${label}-${ts()}.csv`, 'text/csv',
          )}
        />
        <ExportRow
          label="Outcome Report (CSV)"
          description="Attribution-linked outcome table — all taken/skipped/viewed with context"
          disabled={none || outcomeRows.length === 0}
          onExport={() => downloadBlob(
            buildOutcomeReportCSV(outcomeRows),
            `MER-outcome-report-${label}-${ts()}.csv`, 'text/csv',
          )}
        />
      </div>

      {/* ── JSON exports ── */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 9, color: 'var(--t3)', fontFamily: 'var(--mono)',
          textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 8 }}>
          JSON Exports
        </div>
        <ExportRow
          label="Activity Log (JSON)"
          description="Full journal entries with all fields including ranked snapshots"
          disabled={none}
          onExport={() => exportJournalJSON(activeEntries)}
        />
        <ExportRow
          label="Discipline Score (JSON)"
          description="Score breakdown with rule counts, totals, grade, and observations"
          disabled={none}
          onExport={() => exportDisciplineJSON({
            exportedAt: new Date().toISOString(),
            version: '7.1C',
            source: 'MER Algo Footprints — Discipline Score',
            cappedScore: disciplineScore,
            rawScore: rules.reduce((s, r) => s + r.total, 0),
            grade: disciplineGrade,
            entryCount: activeEntries.length,
            rules,
            observations,
          })}
        />
        <ExportRow
          label="Executive Summary (JSON)"
          description="Concise session summary: symbol count, action totals, discipline, tags"
          disabled={none}
          onExport={() => downloadBlob(
            JSON.stringify({ ...execSummary, exportedAt: new Date().toISOString() }, null, 2),
            `MER-executive-summary-${label}-${ts()}.json`, 'application/json',
          )}
        />
        <ExportRow
          label="Session Bundle (JSON)"
          description="Complete package: summary, all entries, discipline, outcome report, tags"
          disabled={none}
          onExport={() => downloadBlob(
            JSON.stringify({
              exportedAt:    new Date().toISOString(),
              version:       '7.1C',
              source:        'MER Algo Footprints — Session Bundle',
              disclaimer:    'Read-only export of operator activity. No engine recomputation.',
              session:       summary,
              executive:     execSummary,
              entries:       activeEntries,
              outcomeReport: outcomeRows,
              discipline: {
                score: disciplineScore, grade: disciplineGrade, rules, observations,
              },
            }, null, 2),
            `MER-session-bundle-${label}-${ts()}.json`, 'application/json',
          )}
        />
      </div>

      {/* Footer note */}
      <div style={{
        fontSize: 8, color: 'var(--t3)', lineHeight: 1.5, fontStyle: 'italic',
        borderTop: '1px solid var(--surface)', paddingTop: 8,
      }}>
        All exports are client-side downloads. No data is sent to any server.
        Exported data reflects stored journal activity — no engine recomputation occurs.
      </div>
    </div>
  );
}
